"use client";

import { create } from "zustand";

import type {
  InstantAssistantContextCard,
  InstantAssistantDiscoveryUpdatePayload,
  InstantAssistantMessage,
  InstantAssistantMessageResponse,
  InstantAssistantQueueState,
  InstantAssistantStatusEvent,
  InstantAssistantSessionResponse,
  InstantAssistantToolCallEvent,
  InstantAssistantVoiceSessionResponse,
} from "@/types/assistant";

const MAX_TRACKED_TOOL_CALLS = 200;
import { AssistantDiscoveryUiState, initialDiscoveryState, mergeDiscoveryUpdates } from "./discovery-state";

export type VoiceConnectionState = 'idle' | 'connecting' | 'connected' | 'error';

export type InstantAssistantStore = {
  featureEnabled: boolean;
  voiceFeatureEnabled: boolean;
  namespaceId: string | null;
  operatorId: string;
  operatorName: string;
  operatorAvatarUrl: string | null;
  sessionId: string | null;
  voiceSession: InstantAssistantVoiceSessionResponse | null;
  isVoiceConnecting: boolean;
  voiceConnectionState: VoiceConnectionState;
  voiceError: string | null;
  voiceMuted: boolean;
  voiceLevel: number;
  voiceStartedAt: string | null;
  isPanelOpen: boolean;
  isSending: boolean;
  isStreaming: boolean;
  messages: InstantAssistantMessage[];
  /**
   * Live tool-call events for the current session. Not persisted; cleared on
   * session restart and capped at MAX_TRACKED_TOOL_CALLS to bound memory.
   */
  toolCalls: InstantAssistantToolCallEvent[];
  queue: InstantAssistantQueueState | null;
  composerDraft: string;
  contextCards: InstantAssistantContextCard[];
  manualContextCards: InstantAssistantContextCard[];
  autoContextSources: Record<string, InstantAssistantContextCard[]>;
  dismissedAutoContextSourceIds: string[];
  discoveryUi: AssistantDiscoveryUiState;
  onboardingCompleted: boolean;
  errorMessage: string | null;
  pendingDiscoveryUpdate: InstantAssistantDiscoveryUpdatePayload | null;
  status: InstantAssistantStatusEvent;
  hasSelection: boolean;
  focusReleaseSignal: number;
  actions: {
    setFeatureEnabled: (enabled: boolean) => void;
    setVoiceFeatureEnabled: (enabled: boolean) => void;
    setNamespaceId: (namespaceId: string | null) => void;
    setOperatorProfile: (profile: { id: string; name?: string | null; avatarUrl?: string | null }) => void;
    resetOperatorProfile: () => void;
    openPanel: () => void;
    closePanel: () => void;
    togglePanel: () => void;
    setComposerDraft: (value: string) => void;
    markSending: (value: boolean) => void;
    markStreaming: (value: boolean) => void;
    hydrateSession: (response: InstantAssistantSessionResponse) => void;
    applyMessageResponse: (response: InstantAssistantMessageResponse) => void;
    appendMessage: (message: InstantAssistantMessage) => void;
    appendToolCall: (event: InstantAssistantToolCallEvent) => void;
    setQueue: (queue: InstantAssistantQueueState) => void;
    prependHistory: (history: InstantAssistantMessage[]) => void;
    setContextCards: (cards: InstantAssistantContextCard[]) => void;
    setAutoContextSource: (sourceId: string, cards: InstantAssistantContextCard[]) => void;
    clearAutoContextSource: (sourceId: string) => void;
    removeContextCard: (sourceId: string) => void;
    clearContext: () => void;
    setDiscoveryUi: (
      partial: Partial<AssistantDiscoveryUiState>,
      options?: { persist?: boolean; markGlowExpired?: boolean; resetGlowTimer?: boolean },
    ) => void;
    setError: (message: string | null) => void;
    restartConversation: () => void;
    resetState: () => void;
    clearHistory: () => void;
    clearPendingDiscoveryUpdate: () => void;
    recordLauncherSession: () => void;
    captureSelectionContext: () => void;
    setStatus: (status: InstantAssistantStatusEvent) => void;
    setHasSelection: (hasSelection: boolean) => void;
    requestFocusRelease: () => void;
    setVoiceSession: (session: InstantAssistantVoiceSessionResponse | null) => void;
    setVoiceConnecting: (value: boolean) => void;
    setVoiceConnectionState: (state: VoiceConnectionState) => void;
    setVoiceError: (message: string | null) => void;
    setVoiceMuted: (muted: boolean) => void;
    setVoiceLevel: (level: number) => void;
    setVoiceStartedAt: (timestamp: string | null) => void;
    resetVoiceConnection: () => void;
  };
};

const operatorKey = "merc-assistant-operator-id";

function resolveOperatorId(): { id: string; name: string } {
  if (typeof window === "undefined") {
    return { id: "anonymous", name: "Operator" };
  }

  const stored = window.localStorage.getItem(operatorKey);
  if (stored) {
    return { id: stored, name: "Operator" };
  }

  const generated = crypto.randomUUID();
  window.localStorage.setItem(operatorKey, generated);
  return { id: generated, name: "Operator" };
}

const { id: defaultOperatorId, name: defaultOperatorName } = resolveOperatorId();

export const useInstantAssistantStore = create<InstantAssistantStore>((set, get) => ({
  featureEnabled: false,
  voiceFeatureEnabled: false,
  namespaceId: null,
  operatorId: defaultOperatorId,
  operatorName: defaultOperatorName,
  operatorAvatarUrl: null,
  sessionId: null,
  voiceSession: null,
  isVoiceConnecting: false,
  voiceConnectionState: 'idle',
  voiceError: null,
  voiceMuted: false,
  voiceLevel: 0,
  voiceStartedAt: null,
  isPanelOpen: false,
  isSending: false,
  isStreaming: false,
  messages: [],
  toolCalls: [],
  queue: null,
  composerDraft: "",
  contextCards: [],
  manualContextCards: [],
  autoContextSources: {},
  dismissedAutoContextSourceIds: [],
  discoveryUi: { ...initialDiscoveryState },
  onboardingCompleted: false,
  errorMessage: null,
  pendingDiscoveryUpdate: null,
  status: { kind: "idle" },
  hasSelection: false,
  focusReleaseSignal: 0,
  actions: {
    setFeatureEnabled: (enabled) => set({ featureEnabled: enabled }),
    setVoiceFeatureEnabled: (enabled) => set({ voiceFeatureEnabled: enabled }),
    setNamespaceId: (namespaceId) =>
      set((state) =>
        state.namespaceId === namespaceId
          ? { namespaceId }
          : {
              namespaceId,
              manualContextCards: [],
              autoContextSources: {},
              dismissedAutoContextSourceIds: [],
              contextCards: [],
            },
      ),
    setOperatorProfile: ({ id, name, avatarUrl }) =>
      set((state) => {
        const normalizedName = name?.trim?.() ? name.trim() : defaultOperatorName;
        const normalizedAvatar = avatarUrl?.trim?.() ? avatarUrl.trim() : null;
        if (
          state.operatorId === id &&
          state.operatorName === normalizedName &&
          state.operatorAvatarUrl === normalizedAvatar
        ) {
          return {};
        }

        return {
          operatorId: id,
          operatorName: normalizedName,
          operatorAvatarUrl: normalizedAvatar,
        };
      }),
    resetOperatorProfile: () =>
      set(() => ({
        operatorId: defaultOperatorId,
        operatorName: defaultOperatorName,
        operatorAvatarUrl: null,
      })),
    openPanel: () => {
      const { recordLauncherSession, setDiscoveryUi } = get().actions;
      recordLauncherSession();
      set({ isPanelOpen: true });
      setDiscoveryUi({ showGlow: false, showTooltip: false });
    },
    closePanel: () => set({ isPanelOpen: false }),
    togglePanel: () => {
      const wasOpen = get().isPanelOpen;
      if (wasOpen) {
        set({ isPanelOpen: false });
        return;
      }

      const { recordLauncherSession, setDiscoveryUi } = get().actions;
      recordLauncherSession();
      set({ isPanelOpen: true });
      setDiscoveryUi({ showGlow: false, showTooltip: false });
    },
    setComposerDraft: (value) => set({ composerDraft: value }),
    markSending: (value) => set({ isSending: value }),
    markStreaming: (value) => set({ isStreaming: value }),
    hydrateSession: (response) =>
      set((state) => {
        const showGlow =
          !response.discovery.shortcutAcknowledgedAtUtc &&
          !response.discovery.glowAutoExpiredAtUtc;
        const discoveryUi: AssistantDiscoveryUiState = {
          showGlow,
          showTooltip: !response.discovery.launcherTooltipDismissedAtUtc,
          showOverlay: !response.discovery.overlayCompletedAtUtc,
          glowFirstSeenAt:
            state.discoveryUi.glowFirstSeenAt ??
            (showGlow ? new Date().toISOString() : state.discoveryUi.glowFirstSeenAt ?? null),
          sessionsSinceHint: state.discoveryUi.sessionsSinceHint ?? 0,
        };

        if (!showGlow) {
          discoveryUi.sessionsSinceHint = 0;
        }

        return {
          sessionId: response.sessionId,
          messages: state.messages.length ? state.messages : response.transcript ?? [],
          queue: response.queue,
          onboardingCompleted: response.onboardingCompleted,
          discoveryUi,
          errorMessage: null,
          pendingDiscoveryUpdate: null,
        };
      }),
    applyMessageResponse: (response) =>
      set((state) => ({
        messages: [...state.messages, response.message],
        queue: response.queue,
      })),
    appendMessage: (message) =>
      set((state) => ({
        messages: [...state.messages, message],
      })),
    appendToolCall: (event) =>
      set((state) => {
        if (event.eventId && state.toolCalls.some((t) => t.eventId === event.eventId)) {
          return state;
        }
        const next = [...state.toolCalls, event];
        const trimmed = next.length > MAX_TRACKED_TOOL_CALLS
          ? next.slice(next.length - MAX_TRACKED_TOOL_CALLS)
          : next;
        return { toolCalls: trimmed };
      }),
    setQueue: (queue) => set({ queue }),
    prependHistory: (history) =>
      set((state) => ({
        messages: history.length ? history.concat(state.messages).slice(-50) : state.messages,
      })),
    setContextCards: (cards) =>
      set((state) => {
        const manualContextCards = dedupeContextCards(cards);
        return {
          manualContextCards,
          contextCards: rebuildContextCards(
            manualContextCards,
            state.autoContextSources,
            state.dismissedAutoContextSourceIds,
          ),
        };
      }),
    setAutoContextSource: (sourceId, cards) =>
      set((state) => {
        const nextAutoSources = { ...state.autoContextSources };
        if (!cards.length) {
          delete nextAutoSources[sourceId];
        } else {
          nextAutoSources[sourceId] = cards.map((card) => ensureAutoMetadata(card));
        }

        return {
          autoContextSources: nextAutoSources,
          contextCards: rebuildContextCards(
            state.manualContextCards,
            nextAutoSources,
            state.dismissedAutoContextSourceIds,
          ),
        };
      }),
    clearAutoContextSource: (sourceId) =>
      set((state) => {
        if (!(sourceId in state.autoContextSources)) {
          return {};
        }

        const nextAutoSources = { ...state.autoContextSources };
        delete nextAutoSources[sourceId];

        return {
          autoContextSources: nextAutoSources,
          contextCards: rebuildContextCards(
            state.manualContextCards,
            nextAutoSources,
            state.dismissedAutoContextSourceIds,
          ),
        };
      }),
    removeContextCard: (sourceId) =>
      set((state) => {
        const target = state.contextCards.find((card) => card.sourceId === sourceId);
        if (!target) {
          return {};
        }

        const autoId = getAutoSourceId(target);
        const isAuto = (target.metadata?.source ?? "manual") === "auto" || Boolean(autoId);
        if (isAuto && autoId) {
          const nextDismissed = Array.from(new Set([...state.dismissedAutoContextSourceIds, autoId]));
          return {
            dismissedAutoContextSourceIds: nextDismissed,
            contextCards: rebuildContextCards(
              state.manualContextCards,
              state.autoContextSources,
              nextDismissed,
            ),
          };
        }

        const manualContextCards = state.manualContextCards.filter((card) => card.sourceId !== sourceId);
        return {
          manualContextCards,
          contextCards: rebuildContextCards(
            manualContextCards,
            state.autoContextSources,
            state.dismissedAutoContextSourceIds,
          ),
        };
      }),
    clearContext: () =>
      set((state) => ({
        manualContextCards: [],
        dismissedAutoContextSourceIds: [],
        contextCards: rebuildContextCards([], state.autoContextSources, []),
      })),
    setDiscoveryUi: (partial, options) =>
      set((state) => {
        const nextDiscovery: AssistantDiscoveryUiState = {
          ...state.discoveryUi,
          ...partial,
        };

        if (options?.resetGlowTimer) {
          nextDiscovery.glowFirstSeenAt = new Date().toISOString();
          nextDiscovery.sessionsSinceHint = 0;
        }

        if (!nextDiscovery.showGlow) {
          nextDiscovery.sessionsSinceHint = 0;
        }

        if (options?.persist === false) {
          return { discoveryUi: nextDiscovery };
        }

        const delta: InstantAssistantDiscoveryUpdatePayload = {};
        if (state.discoveryUi.showGlow && !nextDiscovery.showGlow) {
          if (options?.markGlowExpired) {
            delta.glowExpired = true;
          } else {
            delta.shortcutAcknowledged = true;
          }
        }
        if (state.discoveryUi.showTooltip && !nextDiscovery.showTooltip) {
          delta.launcherTooltipDismissed = true;
        }
        if (state.discoveryUi.showOverlay && !nextDiscovery.showOverlay) {
          delta.overlayCompleted = true;
        }

        const pendingDiscoveryUpdate = mergeDiscoveryUpdates(state.pendingDiscoveryUpdate, delta);
        return pendingDiscoveryUpdate !== state.pendingDiscoveryUpdate
          ? { discoveryUi: nextDiscovery, pendingDiscoveryUpdate }
          : { discoveryUi: nextDiscovery };
      }),
    setError: (message) => set({ errorMessage: message }),
    restartConversation: () =>
      set(() => ({
        sessionId: null,
        messages: [],
        toolCalls: [],
        queue: null,
        composerDraft: "",
        errorMessage: null,
        isStreaming: false,
        isSending: false,
        pendingDiscoveryUpdate: null,
        manualContextCards: [],
        autoContextSources: {},
        dismissedAutoContextSourceIds: [],
        contextCards: [],
        status: { kind: "idle" },
        hasSelection: false,
      })),
    resetState: () =>
      set(() => ({
        sessionId: null,
        messages: [],
        toolCalls: [],
        queue: null,
        composerDraft: "",
        errorMessage: null,
        isStreaming: false,
        isSending: false,
        pendingDiscoveryUpdate: null,
        manualContextCards: [],
        autoContextSources: {},
        dismissedAutoContextSourceIds: [],
        contextCards: [],
        voiceSession: null,
        isVoiceConnecting: false,
        voiceConnectionState: 'idle',
        voiceError: null,
        voiceMuted: false,
        voiceLevel: 0,
        voiceStartedAt: null,
      })),
    clearHistory: () => set({ messages: [], toolCalls: [] }),
    clearPendingDiscoveryUpdate: () => set({ pendingDiscoveryUpdate: null }),
    recordLauncherSession: () => {
      set((state) => ({
        discoveryUi: {
          ...state.discoveryUi,
          glowFirstSeenAt: state.discoveryUi.glowFirstSeenAt ?? new Date().toISOString(),
          sessionsSinceHint: (state.discoveryUi.sessionsSinceHint ?? 0) + 1,
        },
      }));

      const discovery = get().discoveryUi;
      if (discovery.showGlow && hasGlowAutoExpired(discovery)) {
        get().actions.setDiscoveryUi({ showGlow: false, showTooltip: false }, { markGlowExpired: true });
      }
    },
    captureSelectionContext: () => {
      if (typeof window === "undefined") {
        return;
      }

      const selection = window.getSelection()?.toString().trim();
      if (!selection) {
        return;
      }

      const snippet = selection.length > 280 ? `${selection.slice(0, 277)}…` : selection;
      const hash = hashString(selection);
      const card: InstantAssistantContextCard = {
        kind: "selection",
        title: "Selected text",
        subtitle: snippet,
        sourceId: `selection:${hash}`,
        metadata: {
          source: "selection",
          selectionHash: hash,
        },
      };

      set((state) => {
        const exists = state.manualContextCards.some((existing) => existing.sourceId === card.sourceId);
        const updatedDraft = state.composerDraft.trim().length
          ? `${state.composerDraft}\n> ${selection}\n\n`
          : `> ${selection}\n\n`;

        return exists
          ? {
              composerDraft: updatedDraft,
              hasSelection: false,
            }
          : {
              manualContextCards: [...state.manualContextCards, card],
              contextCards: rebuildContextCards(
                [...state.manualContextCards, card],
                state.autoContextSources,
                state.dismissedAutoContextSourceIds,
              ),
              composerDraft: updatedDraft,
              hasSelection: false,
            };
      });
      if (typeof window !== "undefined") {
        window.getSelection()?.removeAllRanges();
      }
    },
    setStatus: (status) => set({ status }),
    setHasSelection: (hasSelection) => set({ hasSelection }),
    requestFocusRelease: () => set((state) => ({ focusReleaseSignal: state.focusReleaseSignal + 1 })),
    setVoiceSession: (session) => set({ voiceSession: session }),
    setVoiceConnecting: (value) => set({ isVoiceConnecting: value }),
    setVoiceConnectionState: (state) => set({ voiceConnectionState: state }),
    setVoiceError: (message) => set({ voiceError: message }),
    setVoiceMuted: (muted) => set({ voiceMuted: muted }),
    setVoiceLevel: (level) => set({ voiceLevel: level }),
    setVoiceStartedAt: (timestamp) => set({ voiceStartedAt: timestamp }),
    resetVoiceConnection: () =>
      set({
        voiceConnectionState: 'idle',
        voiceError: null,
        voiceMuted: false,
        voiceLevel: 0,
        voiceStartedAt: null,
        isVoiceConnecting: false,
      }),
  },
}));

function hasGlowAutoExpired(discovery: AssistantDiscoveryUiState) {
  const sessions = discovery.sessionsSinceHint ?? 0;
  if (sessions >= 3) {
    return true;
  }

  const firstSeen = discovery.glowFirstSeenAt ? Date.parse(discovery.glowFirstSeenAt) : NaN;
  if (Number.isNaN(firstSeen)) {
    return false;
  }

  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  return Date.now() - firstSeen >= sevenDaysMs;
}

function dedupeContextCards(cards: InstantAssistantContextCard[]) {
  const seen = new Set<string>();
  const normalized: InstantAssistantContextCard[] = [];

  for (const card of cards) {
    const key = card.sourceId ?? `${card.kind}:${card.title ?? ""}:${card.subtitle ?? ""}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    normalized.push(card);
  }

  return normalized;
}

function ensureAutoMetadata(card: InstantAssistantContextCard): InstantAssistantContextCard {
  const metadata = {
    ...(card.metadata ?? {}),
    source: card.metadata?.source ?? "auto",
  } as Record<string, string>;

  if (!metadata.autoSourceId && card.sourceId) {
    metadata.autoSourceId = card.sourceId;
  }

  return { ...card, metadata };
}

function getAutoSourceId(card: InstantAssistantContextCard) {
  return card.metadata?.autoSourceId ?? card.sourceId ?? null;
}

function hashString(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

function rebuildContextCards(
  manualCards: InstantAssistantContextCard[],
  autoSources: Record<string, InstantAssistantContextCard[]>,
  dismissedAutoIds: string[],
) {
  const allowedAutoCards = Object.values(autoSources)
    .flat()
    .map((card) => ensureAutoMetadata(card))
    .filter((card) => {
      const autoId = getAutoSourceId(card);
      return autoId ? !dismissedAutoIds.includes(autoId) : true;
    });

  return dedupeContextCards([...manualCards, ...allowedAutoCards]);
}
